import { FormikProps } from 'formik';
import { checkedLocalization } from 'utils/checkedLocalization';
import {
  IOnSubmitParams,
  ISettings,
  TCurrentFilterType,
  TGroupingItem,
  TGroupingsState,
  TNextStepData,
  TObjectionTypes,
} from './types';

export const customFormikSetField = <T>(
  selectOptions: Record<string, string>[],
  setFieldValue: FormikProps<T>['setFieldValue'],
  key: string,
  value: string,
): void => {
  const valueFromOptions = selectOptions.find(
    (option) => option.value === value,
  );

  setFieldValue(key, valueFromOptions);
};

/**
 *
 * @param report Конкретные настройки из метода getReportSettings - по filtering или grouping
 * @returns Универсальный объект из которого выводим динамические инпуты в фильтрах
 */
export const getCurrentReportSettings = (
  report: ISettings[],
): TGroupingsState => {
  const newGroupings: TGroupingsState = {};

  const translateString = (value: string): string => checkedLocalization(`reportbuilder.groupings.${value}`, value, false);

  report.forEach((settings: ISettings) => {
    const { title, source_type, data, source, sql_key } = settings;
    let result: TCurrentFilterType | null = null;

    switch (source_type) {
      case 'catalog':
      case 'target': {
        const selectOptions = data.map((item) => ({
          label: translateString(item.title),
          value: item.key,
        }));
        result = selectOptions;
        break;
      }

      case 'no': {
        result = false;
        break;
      }

      case 'dynamic_catalog': {
        result = { [title]: [''] };
        break;
      }

      default: {
        break;
      }
    }

    if (result === null) return;

    newGroupings[title] = {
      type: source_type,
      label: translateString(title),
      data: result,
      source,
      sql_key,
    };
  });

  return newGroupings;
};

/**
 *
 * @param currentValue Текущее значения поля - строка с internal_id
 * @param currentObjData Данные, которые мы имеем по каждому ключу (agency\client\campaign\creative) из бэка
 * @returns Строка xxhash, которую мы получили по internal_id
 */
export const changeInternalIdToXxhash = (
  currentValue: string | undefined,
  currentObjData: TNextStepData[],
): string => {
  const xxhashParams = currentValue
    ?.split(',')
    .map((val) =>
      currentObjData.reduce((acc, curr) => {
        if (val === curr.internal_id) acc.push(curr.xxhash);
        return acc;
      }, [] as string[]),
    )
    .join(',');

  return xxhashParams || '';
};

/**
 *
 * @param title По сути наш ключ в форме
 * @param settings Объект настроек с бэка
 * @returns source
 */
export const getSqlKeyByTitle = (
  title: string,
  settings: ISettings[] | undefined,
): string => {
  const setting = settings?.find((s) => s.title === title);
  return (setting && setting?.sql_key) || '';
};

/**
 *
 * @param title По сути наш ключ в форме
 * @param settings Объект настроек с бэка
 * @returns source_type
 */
export const getTypeByTitle = (
  title: string,
  settings: ISettings[] | undefined,
): string => {
  const setting = settings?.find((s) => s.title === title);
  return setting?.source_type || '';
};

/**
 *
 * @param params Объект params из onSubmit
 * @param filterType groupBy / filterBy
 * @returns обновленный params
 */
export const setFiltersToParams = (
  params: IOnSubmitParams,
  settings: ISettings[] | undefined,
  filterType: Record<string, any>,
  key: 'grouping' | 'filtering',
): IOnSubmitParams => {
  const result = { ...params };

  Object.entries(filterType).forEach(([title, fieldsData]) => {
    const itemKey = getSqlKeyByTitle(title, settings);
    if (!itemKey) {
      return;
    }

    const type = getTypeByTitle(title, settings);

    if (type === 'dynamic_catalog') {
      result[key][itemKey] = fieldsData;
    }
    if (type === 'target') {
      result[key][itemKey] = fieldsData.split(',');
    }
    if (type === 'catalog') {
      result[key][itemKey] =
        key === 'filtering' ? fieldsData.split(',') : fieldsData.value;
    }
    if (type === 'no') {
      if (fieldsData) {
        result[key][itemKey] = fieldsData;
      }
    }
  });

  return result;
};

/**
 *
 * @param params Объект params из onSubmit
 * @param objectionsBy Ключи objectionBy из формы
 * @param objections Состояние для objection
 * @returns обновленный params
 */
export const getCurrentObjection = (
  params: IOnSubmitParams,
  objectionsBy: Partial<Record<TObjectionTypes, string>>,
  objections: Record<TObjectionTypes, TGroupingItem>,
): IOnSubmitParams => {
  const result = { ...params };
  const creativeData = objections.creative?.data;
  const campaignData = objections.campaign?.data;
  const clientData = objections.client?.data;
  const agencyData = objections.agency?.data;

  if (objectionsBy.creative && isNextStepDataArray(creativeData)) {
    result.objection.creative = changeInternalIdToXxhash(
      objectionsBy.creative,
      creativeData,
    ).split(',');
  } else if (objectionsBy.campaign && isNextStepDataArray(campaignData)) {
    result.objection.campaign = changeInternalIdToXxhash(
      objectionsBy.campaign,
      campaignData,
    ).split(',');
  } else if (objectionsBy.client && isNextStepDataArray(clientData)) {
    result.objection.client = changeInternalIdToXxhash(
      objectionsBy.client,
      clientData,
    ).split(',');
  } else if (objectionsBy.agency && isNextStepDataArray(agencyData)) {
    result.objection.agency = changeInternalIdToXxhash(
      objectionsBy.agency,
      agencyData,
    ).split(',');
  }

  return result;
};

/**
 * Добавляет разделитель в виде пробела
 * @param integer Данные конкретной статы
 * @param columnId Ключ статы
 * @param statistics Массив ключей статы
 * @returns Строка статы с разделителем в виде пробела
 */
export const addSpacesForIntegers = (
  integer: number | string,
  columnId: string,
  statistics: string[],
): string => {
  const value = String(integer);
  const match = value.match(/^(-?)(\d+)([.,]\d+)?$/);

  if (match && statistics.includes(columnId)) {
    const [, sign, wholePart, fractionalPart = ''] = match;
    const formattedWholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return `${sign}${formattedWholePart}${fractionalPart}`;
  }

  return value;
};

export const isObjectionData = (
  objection: TCurrentFilterType | null,
): objection is TNextStepData[] => {
  if (!Array.isArray(objection) || objection.length === 0) return false;

  return objection.every(
    (item) =>
      item !== null &&
      typeof item === 'object' &&
      'xxhash' in item &&
      'title' in item &&
      'internal_id' in item,
  );
};

export const isNextStepData = (item: unknown): item is TNextStepData =>
  item !== null &&
  typeof item === 'object' &&
  'xxhash' in item &&
  'internal_id' in item &&
  'title' in item;

export const isNextStepDataArray = (value: unknown): value is TNextStepData[] =>
  Array.isArray(value) && value.every(isNextStepData);
